import { Matrix4 } from 'three';

/** A direction or point as three numbers. */
export type Vec3 = readonly [number, number, number];

/**
 * How far past the critical angle a facet has to lean before it counts as
 * needing support. A facet lying exactly on the critical angle is
 * self-supporting, which is where the auto-orient optimum tends to settle, so
 * floating-point noise must not tip it over.
 */
export const SUPPORT_EPSILON = 1e-5;

/** Per-triangle data the support area is summed from, in the viewer frame. */
export interface SupportFacets {
  /** Unit outward normal of each triangle, xyz interleaved; zero for a degenerate triangle. */
  readonly normals: Float32Array;
  /** True surface area of each triangle [mm²]. */
  readonly areas: Float32Array;
  /** Sum of `areas` [mm²]. */
  readonly totalArea: number;
}

export interface SupportArea {
  /** Surface area that needs support [mm²]. */
  readonly supportAreaMm2: number;
  /** Total surface area [mm²]. */
  readonly totalAreaMm2: number;
  /** supportAreaMm2 as a share of totalAreaMm2 [%]; 0 when there is no surface. */
  readonly percent: number;
}

/**
 * Normals and areas of every triangle, taken after `matrix` places the
 * vertices (identity when omitted). Triangles are counter-clockwise seen from
 * outside. Worth doing once per mesh: evaluating a direction afterwards is one
 * dot product per triangle.
 */
export function supportFacets(
  positions: ArrayLike<number>,
  index: ArrayLike<number> | null,
  matrix?: Matrix4 | null,
): SupportFacets {
  const count = Math.floor((index ? index.length : positions.length / 3) / 3);
  const normals = new Float32Array(count * 3);
  const areas = new Float32Array(count);
  const m = matrix ? matrix.elements : null;
  const p = new Float64Array(9);
  let totalArea = 0;

  for (let t = 0; t < count; t++) {
    for (let c = 0; c < 3; c++) {
      const v = index ? index[t * 3 + c] : t * 3 + c;
      const x = positions[v * 3];
      const y = positions[v * 3 + 1];
      const z = positions[v * 3 + 2];
      if (m) {
        p[c * 3] = m[0] * x + m[4] * y + m[8] * z + m[12];
        p[c * 3 + 1] = m[1] * x + m[5] * y + m[9] * z + m[13];
        p[c * 3 + 2] = m[2] * x + m[6] * y + m[10] * z + m[14];
      } else {
        p[c * 3] = x;
        p[c * 3 + 1] = y;
        p[c * 3 + 2] = z;
      }
    }
    const ux = p[3] - p[0], uy = p[4] - p[1], uz = p[5] - p[2];
    const vx = p[6] - p[0], vy = p[7] - p[1], vz = p[8] - p[2];
    const nx = uy * vz - uz * vy;
    const ny = uz * vx - ux * vz;
    const nz = ux * vy - uy * vx;
    const length = Math.sqrt(nx * nx + ny * ny + nz * nz);
    if (!(length > 0)) continue;
    normals[t * 3] = nx / length;
    normals[t * 3 + 1] = ny / length;
    normals[t * 3 + 2] = nz / length;
    const area = length / 2;
    areas[t] = area;
    totalArea += area;
  }
  return { normals, areas, totalArea };
}

/**
 * The value of n·d below which a facet needs support: the facet's outward
 * normal leans further below horizontal than `critAngleDeg` (measured from
 * vertical) allows, less SUPPORT_EPSILON.
 */
export function supportThreshold(critAngleDeg: number): number {
  return -Math.sin((critAngleDeg * Math.PI) / 180) - SUPPORT_EPSILON;
}

/**
 * The support area when the part is built along `direction` (the build
 * plate's normal, pointing up), summed over one or more meshes. The same rule
 * brepsmith reports: a facet needs support when its outward normal n satisfies
 * n·d < −sin(critical angle); its true surface area counts, not the projected
 * one; faces resting on the build plate count too.
 */
export function supportArea(
  facets: readonly SupportFacets[],
  direction: Vec3,
  critAngleDeg: number,
): SupportArea {
  let totalAreaMm2 = 0;
  for (const mesh of facets) totalAreaMm2 += mesh.totalArea;
  const unit = unitDirection(direction);
  if (!unit || !(totalAreaMm2 > 0)) return { supportAreaMm2: 0, totalAreaMm2, percent: 0 };

  const threshold = supportThreshold(critAngleDeg);
  let supportAreaMm2 = 0;
  for (const mesh of facets) supportAreaMm2 += classify(mesh, unit, threshold);
  return { supportAreaMm2, totalAreaMm2, percent: (supportAreaMm2 / totalAreaMm2) * 100 };
}

function unitDirection(direction: Vec3): Vec3 | null {
  const length = Math.hypot(direction[0], direction[1], direction[2]);
  return length > 0 ? [direction[0] / length, direction[1] / length, direction[2] / length] : null;
}

/** The area of the facets that need support along the unit `d`. */
function classify(facets: SupportFacets, d: Vec3, threshold: number): number {
  const { normals, areas } = facets;
  const [dx, dy, dz] = d;
  let area = 0;
  for (let t = 0, n = 0; t < areas.length; t++, n += 3) {
    if (normals[n] * dx + normals[n + 1] * dy + normals[n + 2] * dz < threshold) area += areas[t];
  }
  return area;
}

/** How much less support `after` needs than `before` [%]: positive is better, 0 when `before` is 0. */
export function supportImprovementPct(beforeMm2: number, afterMm2: number): number {
  return beforeMm2 > 0 ? (100 * (beforeMm2 - afterMm2)) / beforeMm2 : 0;
}
